import { ActionModal } from '@kcftech/design-system';
import { useMutation } from '@tanstack/react-query';
import { FeedbackPopupBody, FeedbackPopupInput } from 'components/chatbot/styled';
import { HelpBotPayload, MessageInfo } from 'components/chatbot/types';
import { usePendoTrackChatbot } from 'components/chatbot/usePendoTrackChatbot';
import { Checkbox } from 'components/forms/Checkbox';
import { useCurrentUserInfo } from 'custom_hooks/data_hooks/user/useCurrentUserInfo';
import { useToggle } from 'custom_hooks/useToggle';
import * as React from 'react';
import { useState } from 'react';
import { fetchGatewayWithAuth } from 'utils/fetch';
import { HttpMethod } from 'utils/types';

export type FeedbackType = 'positive' | 'negative';

interface FeedbackModalProps {
    feedbackType: FeedbackType;
    conversation: MessageInfo[];
    onSuccess: () => void;
    onCancel: () => void;
}

interface FeedbackPayload {
    userId?: string;
    userRole?: string;
    systemId: string;
    conversation: { messages: HelpBotPayload[] };
    isPositiveFeedback: boolean;
    feedbackMessage?: string;
    responseUntrue?: boolean;
    responseUnhelpful?: boolean;
}

async function postHelpbotFeedback(payload: FeedbackPayload): Promise<Response> {
    const path = 'v4/helpbot/feedback';

    return fetchGatewayWithAuth({
        path,
        method: HttpMethod.Post,
        payload,
    });
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({
    feedbackType,
    conversation,
    onSuccess,
    onCancel,
}) => {
    const trackChatbot = usePendoTrackChatbot();
    const { data: currentUser } = useCurrentUserInfo();
    const [responseUntrue, toggleResponseUntrue] = useToggle();
    const [responseUnhelpful, toggleResponseUnhelpful] = useToggle();
    const [feedbackMessage, setFeedbackMessage] = useState('');

    const { mutate: sendFeedback, isPending: isSendingFeedback } = useMutation({
        meta: { name: 'send-feedback' },
        mutationFn: async () => {
            const payload = {
                userId: currentUser?.Id,
                userRole: currentUser?.ApplicationRole,
                systemId: currentUser?.Corporations[0]
                    ? currentUser.Corporations[0].CorporationId
                    : '',
                conversation: {
                    messages: conversation.map((message) => ({
                        role: message.role,
                        content: message.content,
                        timestamp: new Date(message.timestamp),
                        feedback: message.feedback
                            ? {
                                  eligible: message.isEligible,
                                  positive: message.feedback === 'positive',
                                  negative: message.feedback === 'negative',
                              }
                            : undefined,
                    })),
                },
                isPositiveFeedback: feedbackType === 'positive',
                feedbackMessage,
                responseUntrue,
                responseUnhelpful,
            };

            trackChatbot({ type: 'Feedback Submitted', feedback: feedbackType });

            return postHelpbotFeedback(payload);
        },

        onSuccess,
        onError: onCancel,
    });

    const handleInputChange = (event: {
        target: { value: React.SetStateAction<string> };
    }) => {
        setFeedbackMessage(event.target.value);
    };

    return (
        <ActionModal
            title="Additional Feedback"
            onClose={() => {
                onCancel();
                trackChatbot({ type: 'Feedback Cancelled' });
            }}
            closable={!isSendingFeedback}
            onAction={sendFeedback}
            actionButtonText="Submit"
            actionButtonLoading={isSendingFeedback}
            actionable={!isSendingFeedback}
            closeButtonText="Cancel"
            height={feedbackType === 'positive' ? 280 : 330}
            width={540}
        >
            {feedbackType === 'positive' ? (
                <FeedbackPopupBody>
                    What did you like about the answer?
                    <FeedbackPopupInput
                        value={feedbackMessage}
                        onChange={handleInputChange}
                        placeholder="Enter a comment"
                        maxLength={1000}
                        autoFocus
                    />
                </FeedbackPopupBody>
            ) : (
                <FeedbackPopupBody>
                    What did you dislike about the answer?
                    <FeedbackPopupInput
                        value={feedbackMessage}
                        onChange={handleInputChange}
                        placeholder="Enter a comment"
                        maxLength={1000}
                        autoFocus
                    />
                    <Checkbox
                        label="It is not accurate"
                        checked={responseUntrue}
                        onClick={toggleResponseUntrue}
                        style={{ margin: '.3rem 0' }}
                    />
                    <Checkbox
                        label="It is not helpful"
                        checked={responseUnhelpful}
                        onClick={toggleResponseUnhelpful}
                    />
                </FeedbackPopupBody>
            )}
        </ActionModal>
    );
};

export { FeedbackModal };
